import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { 
  ClipboardCheck, 
  User, 
  Calendar as CalendarIcon,
  Pill,
  Phone,
  FileText,
  Stethoscope,
  Activity,
  AlertCircle,
  CheckCircle2,
  ArrowLeft,
  Lock,
  Clock,
  Building2,
  CalendarDays
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { Calendar } from '../../components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

export default function CMACheckoutPage() {
  const { checkoutId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [checkout, setCheckout] = useState(null);
  const [encounter, setEncounter] = useState(null);
  const [patient, setPatient] = useState(null);
  const [visitSummary, setVisitSummary] = useState(null);

  // CMA editable fields
  const [formData, setFormData] = useState({
    scheduled_followup_date: '',
    scheduled_followup_type: '',
    pharmacy_verified: false,
    pharmacy_notes: '',
    medication_confirmed: false,
    medication_notes: '',
    additional_tasks_completed: '',
    cma_notes: ''
  });

  const loadCheckoutData = useCallback(async () => {
    try {
      const res = await api.get(`/checkout/${checkoutId}`);
      setCheckout(res.data.checkout);
      setEncounter(res.data.encounter);
      setPatient(res.data.patient);
      setVisitSummary(res.data.visit_summary);

      // Pre-fill form if there's existing data
      if (res.data.checkout?.checkout_data) {
        setFormData(prev => ({
          ...prev,
          ...res.data.checkout.checkout_data
        }));
      }
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error loading checkout:', err);
      }
      toast.error('Failed to load checkout details');
      navigate('/cma/checkout-queue');
    } finally {
      setLoading(false);
    }
  }, [checkoutId, navigate]);

  useEffect(() => {
    loadCheckoutData();
  }, [loadCheckoutData]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const response = await api.post(`/checkout/${checkoutId}/complete`, formData);
      const { email_sent, appointment_scheduled, appointment_conflict } = response.data;
      
      // Build detailed success message
      let successMsg = 'Checkout completed and AVS finalized!';
      const details = [];
      if (email_sent) details.push('Email sent to patient');
      if (appointment_scheduled) details.push('Follow-up appointment scheduled');
      if (appointment_conflict) details.push('Note: Appointment not created (patient already has one that day)');
      if (details.length > 0) {
        successMsg += ' ' + details.join(' • ');
      }
      
      toast.success(successMsg);
      navigate('/cma/checkout-queue');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to complete checkout');
    } finally {
      setSubmitting(false);
    }
  };

  const formatVitals = (vitals) => {
    if (!vitals) return null;
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {vitals.blood_pressure && (
          <div className="bg-white p-2 rounded border">
            <p className="text-xs text-slate-500">Blood Pressure</p>
            <p className="font-semibold">{vitals.blood_pressure}</p>
          </div>
        )}
        {vitals.heart_rate && (
          <div className="bg-white p-2 rounded border">
            <p className="text-xs text-slate-500">Heart Rate</p>
            <p className="font-semibold">{vitals.heart_rate} bpm</p>
          </div>
        )}
        {vitals.temperature && (
          <div className="bg-white p-2 rounded border">
            <p className="text-xs text-slate-500">Temperature</p>
            <p className="font-semibold">{vitals.temperature}°F</p>
          </div>
        )}
        {vitals.respiratory_rate && (
          <div className="bg-white p-2 rounded border">
            <p className="text-xs text-slate-500">Resp. Rate</p>
            <p className="font-semibold">{vitals.respiratory_rate}/min</p>
          </div>
        )}
        {vitals.oxygen_saturation && (
          <div className="bg-white p-2 rounded border">
            <p className="text-xs text-slate-500">O2 Sat</p>
            <p className="font-semibold">{vitals.oxygen_saturation}%</p>
          </div>
        )}
        {vitals.weight && (
          <div className="bg-white p-2 rounded border">
            <p className="text-xs text-slate-500">Weight</p>
            <p className="font-semibold">{vitals.weight} lbs</p>
          </div>
        )}
        {vitals.height && (
          <div className="bg-white p-2 rounded border">
            <p className="text-xs text-slate-500">Height</p>
            <p className="font-semibold">{vitals.height}</p>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  if (!checkout) {
    return (
      <div className="p-6 text-center">
        <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <h2 className="text-xl font-semibold">Checkout not found</h2>
        <Button onClick={() => navigate('/cma/checkout-queue')} className="mt-4">
          Back to Queue
        </Button>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6" data-testid="cma-checkout-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <Button 
            variant="ghost" 
            onClick={() => navigate('/cma/checkout-queue')}
            className="mb-2 -ml-2"
            data-testid="back-to-queue-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Queue
          </Button>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <ClipboardCheck className="h-7 w-7 text-teal-600" />
            Patient Checkout
          </h1>
        </div>
        <Badge 
          className={checkout.status === 'in_progress' 
            ? 'bg-blue-100 text-blue-800' 
            : 'bg-yellow-100 text-yellow-800'
          }
        >
          {checkout.status === 'in_progress' ? 'In Progress' : 'Pending'}
        </Badge>
      </div>

      {/* Patient & Provider Info */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-6">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-full bg-teal-100 flex items-center justify-center">
                <User className="h-6 w-6 text-teal-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Patient</p>
                <p className="font-semibold text-lg" data-testid="checkout-patient-name">{checkout.patient_name}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                <Stethoscope className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Provider</p>
                <p className="font-semibold text-lg">{checkout.provider_name}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-full bg-slate-100 flex items-center justify-center">
                <Clock className="h-6 w-6 text-slate-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Sent to Checkout</p>
                <p className="font-semibold">
                  {new Date(checkout.created_at).toLocaleString()}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Provider Instructions */}
      {checkout.instructions && (
        <Card className="border-amber-200 bg-amber-50" data-testid="provider-instructions-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2 text-amber-800">
              <AlertCircle className="h-5 w-5" />
              Provider Instructions
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {checkout.instructions.return_visit && (
              <div className="flex items-start gap-2">
                <CalendarIcon className="h-5 w-5 text-amber-700 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-800">Return Visit</p>
                  <p className="text-amber-700">{checkout.instructions.return_visit}</p>
                </div>
              </div>
            )}
            {checkout.instructions.scheduling_needs && (
              <div className="flex items-start gap-2">
                <Clock className="h-5 w-5 text-amber-700 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-800">Scheduling Needs</p>
                  <p className="text-amber-700">{checkout.instructions.scheduling_needs}</p>
                </div>
              </div>
            )}
            {checkout.instructions.pharmacy_verification && (
              <div className="flex items-start gap-2">
                <Building2 className="h-5 w-5 text-amber-700 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-800">Pharmacy Verification</p>
                  <p className="text-amber-700">{checkout.instructions.pharmacy_verification}</p>
                </div>
              </div>
            )}
            {checkout.instructions.medication_followup && (
              <div className="flex items-start gap-2">
                <Pill className="h-5 w-5 text-amber-700 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-800">Medication Follow-up</p>
                  <p className="text-amber-700">{checkout.instructions.medication_followup}</p>
                </div>
              </div>
            )}
            {checkout.instructions.additional_notes && (
              <div className="flex items-start gap-2">
                <FileText className="h-5 w-5 text-amber-700 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-800">Additional Notes</p>
                  <p className="text-amber-700">{checkout.instructions.additional_notes}</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column - Read-Only Clinical Info */}
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-slate-600">
            <Lock className="h-4 w-4" />
            <span className="text-sm font-medium">Clinical Information (Read Only)</span>
          </div>

          {/* Vitals */}
          {encounter?.vitals && (
            <Card data-testid="readonly-vitals-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Activity className="h-4 w-4 text-teal-600" />
                  Vitals
                </CardTitle>
              </CardHeader>
              <CardContent>
                {formatVitals(encounter.vitals)}
              </CardContent>
            </Card>
          )}

          {/* Diagnosis */}
          <Card data-testid="readonly-diagnosis-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <FileText className="h-4 w-4 text-teal-600" />
                Diagnosis
              </CardTitle>
            </CardHeader>
            <CardContent>
              {encounter?.diagnoses && encounter.diagnoses.length > 0 ? (
                <ul className="space-y-2">
                  {encounter.diagnoses.map((diag, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <Badge variant="outline" className="font-mono text-xs">
                        {diag.code || 'N/A'}
                      </Badge>
                      <span className="text-sm text-slate-700">{diag.description}</span>
                    </li>
                  ))}
                </ul>
              ) : encounter?.diagnosis ? (
                <p className="text-sm text-slate-700">{encounter.diagnosis}</p>
              ) : (
                <p className="text-sm text-slate-500 italic">No diagnosis recorded</p>
              )}
            </CardContent>
          </Card>

          {/* Treatment Plan */}
          <Card data-testid="readonly-treatment-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Stethoscope className="h-4 w-4 text-teal-600" />
                Treatment Plan
              </CardTitle>
            </CardHeader>
            <CardContent>
              {encounter?.treatment_plan ? (
                <p className="text-sm text-slate-700 whitespace-pre-wrap">{encounter.treatment_plan}</p>
              ) : (
                <p className="text-sm text-slate-500 italic">No treatment plan recorded</p>
              )}
            </CardContent>
          </Card>

          {/* Prescriptions */}
          <Card data-testid="readonly-prescriptions-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Pill className="h-4 w-4 text-teal-600" />
                Prescriptions
              </CardTitle>
            </CardHeader>
            <CardContent>
              {encounter?.prescriptions && encounter.prescriptions.length > 0 ? (
                <ul className="space-y-3">
                  {encounter.prescriptions.map((rx, idx) => {
                    const medName = rx.medication?.name 
                      ? `${rx.medication.name}${rx.medication.strength ? ` ${rx.medication.strength}` : ''}`
                      : (rx.name || rx.medication_name || 'Unknown');
                    return (
                      <li key={idx} className="bg-slate-50 p-3 rounded-lg">
                        <p className="font-medium text-slate-900">{medName}</p>
                        <p className="text-sm text-slate-600">
                          {rx.dosage} - {rx.frequency}
                        </p>
                        {rx.instructions && (
                          <p className="text-sm text-slate-500 mt-1">{rx.instructions}</p>
                        )}
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p className="text-sm text-slate-500 italic">No prescriptions</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Column - CMA Tasks */}
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-teal-700">
            <ClipboardCheck className="h-4 w-4" />
            <span className="text-sm font-medium">Checkout Tasks</span>
          </div>

          {/* Scheduling */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <CalendarDays className="h-4 w-4 text-teal-600" />
                Follow-up Scheduling
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="followup_date">Follow-up Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      data-testid="followup-date-input"
                      className={`w-full mt-1 justify-start text-left font-normal ${
                        !formData.scheduled_followup_date && "text-muted-foreground"
                      }`}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {formData.scheduled_followup_date ? (
                        format(new Date(formData.scheduled_followup_date), "PPP")
                      ) : (
                        <span>Select follow-up date</span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={formData.scheduled_followup_date ? new Date(formData.scheduled_followup_date) : undefined}
                      onSelect={(date) => handleInputChange('scheduled_followup_date', date ? format(date, 'yyyy-MM-dd') : '')}
                      disabled={(date) => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        return date < today;
                      }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <Label htmlFor="followup_type">Appointment Type</Label>
                <Select
                  value={formData.scheduled_followup_type}
                  onValueChange={(value) => handleInputChange('scheduled_followup_type', value)}
                >
                  <SelectTrigger className="mt-1" data-testid="followup-type-select">
                    <SelectValue placeholder="Select type..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="follow_up">Follow-up Visit</SelectItem>
                    <SelectItem value="lab_review">Lab Review</SelectItem>
                    <SelectItem value="medication_check">Medication Check</SelectItem>
                    <SelectItem value="specialist_referral">Specialist Referral</SelectItem>
                    <SelectItem value="physical">Physical Exam</SelectItem>
                    <SelectItem value="telehealth">Telehealth</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Pharmacy Verification */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Building2 className="h-4 w-4 text-teal-600" />
                Pharmacy Verification
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="pharmacy_verified"
                  data-testid="pharmacy-verified-checkbox"
                  checked={formData.pharmacy_verified}
                  onCheckedChange={(checked) => handleInputChange('pharmacy_verified', checked)}
                />
                <Label htmlFor="pharmacy_verified" className="cursor-pointer">
                  Pharmacy verified / prescription sent
                </Label>
              </div>
              <div>
                <Label htmlFor="pharmacy_notes">Pharmacy Notes</Label>
                <Textarea
                  id="pharmacy_notes"
                  data-testid="pharmacy-notes-input"
                  value={formData.pharmacy_notes}
                  onChange={(e) => handleInputChange('pharmacy_notes', e.target.value)}
                  placeholder="E.g., Called CVS, prescription ready for pickup in 2 hours..."
                  className="mt-1"
                  rows={2}
                />
              </div>
            </CardContent>
          </Card>

          {/* Medication Confirmation */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Pill className="h-4 w-4 text-teal-600" />
                Medication Confirmation
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="medication_confirmed"
                  data-testid="medication-confirmed-checkbox"
                  checked={formData.medication_confirmed}
                  onCheckedChange={(checked) => handleInputChange('medication_confirmed', checked)}
                />
                <Label htmlFor="medication_confirmed" className="cursor-pointer">
                  Patient understands medication instructions
                </Label>
              </div>
              <div>
                <Label htmlFor="medication_notes">Medication Notes</Label>
                <Textarea
                  id="medication_notes"
                  data-testid="medication-notes-input"
                  value={formData.medication_notes}
                  onChange={(e) => handleInputChange('medication_notes', e.target.value)}
                  placeholder="E.g., Reviewed side effects, patient has no questions..."
                  className="mt-1"
                  rows={2}
                />
              </div>
            </CardContent>
          </Card>

          {/* Additional Notes */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <FileText className="h-4 w-4 text-teal-600" />
                Additional Notes
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="additional_tasks">Additional Tasks Completed</Label>
                <Textarea
                  id="additional_tasks"
                  data-testid="additional-tasks-input"
                  value={formData.additional_tasks_completed}
                  onChange={(e) => handleInputChange('additional_tasks_completed', e.target.value)}
                  placeholder="E.g., Scheduled referral to cardiology, ordered lab work..."
                  className="mt-1"
                  rows={2}
                />
              </div>
              <div>
                <Label htmlFor="cma_notes">CMA Notes</Label>
                <Textarea
                  id="cma_notes"
                  data-testid="cma-notes-input"
                  value={formData.cma_notes}
                  onChange={(e) => handleInputChange('cma_notes', e.target.value)}
                  placeholder="Any additional notes for the patient record..."
                  className="mt-1"
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>

          {/* Submit Button */}
          <Button 
            onClick={handleSubmit}
            disabled={submitting}
            data-testid="complete-checkout-btn"
            className="w-full bg-teal-600 hover:bg-teal-700 h-12 text-lg"
          >
            {submitting ? (
              <span className="flex items-center gap-2">
                <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" />
                Completing...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5" />
                Complete Checkout &amp; Finalize AVS
              </span>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
